import { useRaisedHands } from "../../../hooks/useRaisedHands";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Video, Calendar, Scale } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { AcceptIcon } from "../../../components/Icons";
import Popover from "../../../components/Popover";
import { useAuth } from "../../../context/AuthProvider";
import { toast } from "react-hot-toast";
import { useMeetingStore } from "../../../store/meeting.store";
import MeetingMediaSoup from "../../../components/mediasoup/Meeting.MediaSoup";
import { socket } from "../../../components/mediasoup/socket";
import logger from "../../../utils/logger";
import clsx from "clsx";
import AdvocateControlBar from "./AdvocateControlBar";
import { usePublicMeetingStore } from "../../../store/public.meeting.store";
import {
  ADVOCATE_LOBBY_PAYLOAD_KEY,
  readStoredPayload,
} from "../../../utils/advocateLobbyJoin";

const getNextCase = (updatedCases: any[]) =>
  updatedCases.find(
    (c: any) => c.status === "Waiting" || c.status === "Judge Invited",
  );

const updateCompletedCase = (prevCases: any[], cnr: string) =>
  prevCases.map((c: any) => ({
    ...c,
    status: c.cnr === cnr ? "Completed" : c.status,
  }));

const IN_MEETING_STATUSES = new Set(["In Meeting", "Joined", "joined"]);
const REQUESTED_STATUSES = new Set(["Waiting", "Judge Invited"]);
const DEFAULT_ADVOCATE_MIC_ON = false;

const getCaseStatus = (status?: string) => status?.trim().toLowerCase() || "";

const isLiveCase = (caseItem?: Pick<CaseItem, "status"> | null) => {
  const status = getCaseStatus(caseItem?.status);
  return status === "live" || status === "live now";
};

const getMeetingId = (caseItem: Pick<CaseItem, "courtRoom" | "courtRoomId">) =>
  `${caseItem.courtRoom}_${caseItem.courtRoomId}`;

interface CaseItem {
  _id: string;
  caseTitle: string;
  cnr: string;
  status:
    | "Scheduled"
    | "Waiting"
    | "In Progress"
    | "Completed"
    | "Cancelled"
    | "Live"
    | "Upcoming"
    | "Live Now"
    | "Pending"
    | "In Meeting"
    | "Joined"
    | "joined"
    | "live"
    | "Declined"
    | "Judge Invited";
  time: string;
  courtRoom: string;
  courtRoomId: string;
  caseNumber: string;
}

type CaseStatus = CaseItem["status"];

const AdvocateLobby: React.FC = () => {
  const { user } = useAuth();
  const caseItem = usePublicMeetingStore((s) => s.caseItem);
  const todaysSchedule = usePublicMeetingStore((state) => state.todaysSchedule);

  const setUserData = useMeetingStore((s) => s.setUserData);
  const localMicOn = useMeetingStore((s) => s.localMicOn);
  const setLocalMicOn = useMeetingStore((s) => s.setLocalMicOn);
  const localMicLocked = useMeetingStore((s) => s.localMicLocked);
  const setLocalMicLocked = useMeetingStore((s) => s.setLocalMicLocked);
  const setAdvocateCases = useMeetingStore((s) => s.setAdvocateCases);
  const advocateCases = useMeetingStore((s) => s.advocateCases);
  const advocteActiveCase = useMeetingStore((s) => s.advocteActiveCase);
  const setAdvocateActiveCase = useMeetingStore((s) => s.setAdvocateActiveCase);
  const routerLocation = useLocation();
  const navigate = useNavigate();

  const [isBlurred, setIsBlurred] = useState(false);
  const [isFrozen, setIsFrozen] = useState(false);
  const frozenFrameRef = useRef<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const initializedCaseSourceRef = useRef("");
  const storedLobbyState = useRef(
    readStoredPayload<{
      cases?: CaseItem[];
      case?: CaseItem;
    }>(ADVOCATE_LOBBY_PAYLOAD_KEY),
  );
  const searchParams = useMemo(
    () => new URLSearchParams(routerLocation.search),
    [routerLocation.search],
  );
  const fullscreenMode = searchParams.get("fullscreen") === "1";

  // Prefer fresh router state (from Prejoin navigation); fall back to the
  // zustand-persisted case(s) so a refresh / remount keeps the lobby working.
  const routerState = routerLocation.state as {
    cases?: CaseItem[];
    case?: CaseItem;
  } | null;
  const location = useMemo(() => {
    const routerCases = routerState?.cases?.length
      ? (routerState.cases as CaseItem[])
      : [];
    const scheduleCases = todaysSchedule?.length
      ? (todaysSchedule as unknown as CaseItem[])
      : [];
    const persistedCases = advocateCases?.cases?.length
      ? (advocateCases.cases as CaseItem[])
      : [];
    const storedCases = storedLobbyState.current?.cases?.length
      ? storedLobbyState.current.cases
      : [];
    const fallbackCases = caseItem ? [caseItem as CaseItem] : [];
    let cases = fallbackCases;
    if (routerCases.length > 0) {
      cases = routerCases;
    } else if (storedCases.length > 0) {
      cases = storedCases;
    } else if (scheduleCases.length > 0) {
      cases = scheduleCases;
    } else if (persistedCases.length > 0) {
      cases = persistedCases;
    }
    const activeCase =
      routerState?.case ??
      storedLobbyState.current?.case ??
      caseItem ??
      cases[0] ??
      null;

    return {
      cases,
      case: activeCase,
    };
  }, [advocateCases?.cases, caseItem, routerState, todaysSchedule]);
  const [cases, setCases] = useState<CaseItem[]>([]);
  const casesRef = useRef<CaseItem[]>([]);
  const activeCaseRef = useRef<CaseItem | null>(null);
  const userRef = useRef(user);

  const [isInMeeting, setIsInMeeting] = useState(false);
  let handRoomId: string | undefined;
  if (isInMeeting) {
    handRoomId = advocteActiveCase?.courtRoomId;
  } else if (advocteActiveCase) {
    handRoomId = getMeetingId(advocteActiveCase);
  }
  const raisedHands = useRaisedHands(handRoomId, !!handRoomId, !isInMeeting);
  const handRaised = raisedHands.some(
    (hand) =>
      String(hand.userId) === String(user?.id) &&
      hand.cnr === advocteActiveCase?.cnr,
  );
  const [handPending, setHandPending] = useState(false);
  const toggleHand = () => {
    if (handPending) return;
    if (!socket.connected) {
      toast.error(
        "Meeting connection lost. Please reconnect before raising your hand.",
      );
      return;
    }
    setHandPending(true);
    socket.timeout(5000).emit(
      isInMeeting ? "set-hand-raised" : "set-lobby-hand-raised",
      {
        raised: !handRaised,
        roomId: handRoomId,
        cnr: advocteActiveCase?.cnr,
      },
      (error: Error | null, result?: { error?: string }) => {
        setHandPending(false);
        if (error || result?.error) {
          toast.error(
            result?.error || "Could not update your hand. Please try again.",
          );
          socket.emit(
            isInMeeting ? "get-raised-hands" : "get-lobby-raised-hands",
            { roomId: handRoomId },
          );
        }
      },
    );
  };
  const [showPopover, setShowPopover] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const skipEmitRef = useRef(false);

  const [micOn, setMicOn] = useState(DEFAULT_ADVOCATE_MIC_ON);
  const [camOn, setCamOn] = useState(true);
  const [cameraId, setCameraId] = useState("");
  const [microphoneId, setMicrophoneId] = useState("");
  const [hasMicPermission, setHasMicPermission] = useState(true);
  const [hasCameraPermission, setHasCameraPermission] = useState(true);

  useEffect(() => {
    casesRef.current = cases;
  }, [cases]);

  useEffect(() => {
    activeCaseRef.current = advocteActiveCase;
  }, [advocteActiveCase]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const commitCases = useCallback(
    (nextCases: CaseItem[]) => {
      casesRef.current = nextCases;
      setCases(nextCases);
      setAdvocateCases({
        socket: socket || null,
        cases: nextCases,
      });
    },
    [setAdvocateCases, socket],
  );

  const videoConstraints = {
    facingMode: "user",
    aspectRatio: {
      ideal: window.innerWidth < window.innerHeight ? 3 / 4 : 16 / 9,
    },
  };

  const audioConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
  };
  const startVideo = async () => {
    try {
      let stream: MediaStream;

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: audioConstraints,
        });
        setHasCameraPermission(stream.getVideoTracks().length > 0);
        setHasMicPermission(stream.getAudioTracks().length > 0);
      } catch (err) {
        logger.log(
          "Microphone permission unavailable. Falling back to audio disabled:",
          err,
        );
        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false,
        });
        setHasCameraPermission(stream.getVideoTracks().length > 0);
        setHasMicPermission(false);
        setMicOn(false);
        setLocalMicOn(false);
        toast.error("Microphone permission is disabled. You will join muted.", {
          id: "advocate-mic-permission",
        });
      }

      localStreamRef.current = stream;
      // Apply current toggle state to freshly acquired tracks.
      stream.getAudioTracks().forEach((t) => (t.enabled = micOn));
      stream.getVideoTracks().forEach((t) => (t.enabled = camOn));
      if (videoRef.current) videoRef.current.srcObject = stream;

      const camTrack = stream.getVideoTracks()[0];
      const micTrack = stream.getAudioTracks()[0];
      if (camTrack) setCameraId(camTrack.getSettings().deviceId || "");
      if (micTrack) setMicrophoneId(micTrack.getSettings().deviceId || "");
    } catch (err) {
      logger.log("Camera/Mic denied:", err);
      setHasCameraPermission(false);
      setHasMicPermission(false);
      setMicOn(false);
      setLocalMicOn(false);
      toast.error("Please enable camera permission.", {
        id: "advocate-camera-permission",
      });
    }
  };

  // Swap a single track in place — keeps the other track and the MediaStream
  // object alive so the <video> never re-attaches → no flicker.
  const swapTrack = async (kind: "audio" | "video", deviceId: string) => {
    const stream = localStreamRef.current;
    if (!stream) return;
    try {
      const tmp = await navigator.mediaDevices.getUserMedia(
        kind === "audio"
          ? { audio: { deviceId: { exact: deviceId } }, video: false }
          : { video: { deviceId: { exact: deviceId } }, audio: false },
      );
      const newTrack = tmp.getTracks()[0];
      if (!newTrack) return;

      const oldTrack =
        kind === "audio"
          ? stream.getAudioTracks()[0]
          : stream.getVideoTracks()[0];
      if (oldTrack) {
        stream.removeTrack(oldTrack);
        oldTrack.stop();
      }
      newTrack.enabled = kind === "audio" ? micOn : camOn;
      stream.addTrack(newTrack);

      // Video swap needs a re-attach; audio does not touch the element.
      if (kind === "video" && videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      logger.log("track swap failed:", err);
      if (kind === "audio") {
        setHasMicPermission(false);
        setMicOn(false);
        setLocalMicOn(false);
        toast.error("Please enable microphone permission.", {
          id: "advocate-mic-permission",
        });
      } else {
        setHasCameraPermission(false);
        setCamOn(false);
        toast.error("Please enable camera permission.", {
          id: "advocate-camera-permission",
        });
      }
    }
  };

  const selectCamera = (device: MediaDeviceInfo) => {
    setCameraId(device.deviceId);
    void swapTrack("video", device.deviceId);
  };

  const selectMicrophone = (device: MediaDeviceInfo) => {
    setMicrophoneId(device.deviceId);
    void swapTrack("audio", device.deviceId);
  };

  const toggleMic = () => {
    if (localMicLocked) {
      return toast.error("Your microphone has been muted by the Judge.", {
        id: "advocate-mic-locked",
      });
    }

    if (!hasMicPermission) {
      return toast.error("Please enable microphone permission.", {
        id: "advocate-mic-permission",
      });
    }
    setMicOn((prev) => {
      const next = !prev;
      localStreamRef.current
        ?.getAudioTracks()
        .forEach((t) => (t.enabled = next));
      setLocalMicOn(next);
      return next;
    });
  };

  const toggleCam = () => {
    if (!hasCameraPermission) {
      return toast.error("Please enable camera permission.", {
        id: "advocate-camera-permission",
      });
    }
    if (camOn)
      return toast.error("You can't turn off your camera in a meeting.", {
        id: "cam-off",
      });
    setCamOn((prev) => {
      const next = !prev;
      localStreamRef.current
        ?.getVideoTracks()
        .forEach((t) => (t.enabled = next));
      return next;
    });
  };

  useEffect(() => {
    setMicOn(localMicOn);
    localStreamRef.current
      ?.getAudioTracks()
      .forEach((track) => (track.enabled = localMicOn));
  }, [localMicOn]);

  const closePopupOrNavigate = useCallback(
    (fallbackPath = "/dashboard") => {
      skipEmitRef.current = true;
      void document.exitFullscreen?.().catch(() => undefined);

      if (window.opener && !window.opener.closed) {
        window.close();
        window.setTimeout(() => navigate(fallbackPath), 250);
        return;
      }

      navigate(fallbackPath);
    },
    [navigate],
  );

  const handleLeave = () => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    setLocalMicLocked(false);
    leaveMeeting();
    closePopupOrNavigate();
  };

  // logger.log("location", location);
  // ------------------------------------------------------
  // 🎥 START LOCAL VIDEO
  // ------------------------------------------------------
  useEffect(() => {
    setUserData({
      _id: user?.id || "",
      name: user?.name || "",
      role: user?.role || "",
      roomNo: advocteActiveCase?.courtRoom || "room-1",
      roomId: advocteActiveCase?.courtRoomId || "room-1",
    });
    setMicOn(DEFAULT_ADVOCATE_MIC_ON);
    setLocalMicOn(DEFAULT_ADVOCATE_MIC_ON);
    setLocalMicLocked(false);

    startVideo();
  }, [user]);

  useEffect(() => {
    if (!fullscreenMode) return;
    void document.documentElement.requestFullscreen?.().catch(() => undefined);
  }, [fullscreenMode]);

  // ------------------------------------------------------
  // 🔄 INITIALIZE CASES
  // ------------------------------------------------------
  useEffect(() => {
    const nextCases = location.cases || [];
    const caseSourceKey = nextCases.map((item) => item.cnr).join("|");

    if (
      !nextCases.length ||
      initializedCaseSourceRef.current === caseSourceKey
    ) {
      return;
    }

    initializedCaseSourceRef.current = caseSourceKey;
    commitCases(nextCases);

    const activeCase = location.case
      ? nextCases.find((ci: CaseItem) => ci.cnr === location.case.cnr) ||
        location.case
      : nextCases[0] || null;

    setAdvocateActiveCase(activeCase || null);
  }, [commitCases, location, setAdvocateActiveCase]);

  // ------------------------------------------------------
  // 🛑 CLEAN EXIT FUNCTION
  // ------------------------------------------------------
  const leaveMeeting = useCallback(() => {
    const currentUser = userRef.current;
    const activeCase = activeCaseRef.current;
    if (!socket || !currentUser?.id) return;

    const requestedCases = casesRef.current.filter((item) =>
      REQUESTED_STATUSES.has(String(item.status)),
    );
    const fallbackCase = activeCase ? [activeCase] : [];
    const casesToLeave = requestedCases.length ? requestedCases : fallbackCase;
    const seen = new Set<string>();

    casesToLeave.forEach((caseItem) => {
      const key = `${caseItem.courtRoomId}:${caseItem.cnr}`;
      if (seen.has(key)) return;
      seen.add(key);

      socket.emit("leave-meeting", {
        meetingId: getMeetingId(caseItem),
        roomNo: caseItem.courtRoom,
        roomId: caseItem.courtRoomId,
        userId: currentUser.id,
        role: currentUser.role,
        cnr: caseItem.cnr,
        reason: "USER_LEFT",
      });
    });
  }, [socket]);

  // ------------------------------------------------------
  // 📌 AUTO REGISTER for room when activeCase changes
  // ------------------------------------------------------
  const handleSendRequest = useCallback(
    (caseItem: any) => {
      logger.log("caseItem", { caseItem, user });
      if (!socket || !user) return;
      socket.emit("advocate_sent_request", {
        meetingId: getMeetingId(caseItem),
        roomNo: `${caseItem.courtRoom}`,
        roomId: `${caseItem.courtRoomId}`,
        userId: user.id,
        role: user.role,
        name: user.name,
        host: false,
        cnr: caseItem.cnr,
        caseNumber: caseItem.caseNumber,
        caseTitle: caseItem.caseTitle,
      });
      const updated = casesRef.current.map((c) =>
        c.cnr === caseItem?.cnr ? { ...c, status: "Waiting" as CaseStatus } : c,
      );
      const updatedCase = updated.find(
        (item) => item.cnr === caseItem?.cnr,
      ) || {
        ...caseItem,
        status: "Waiting" as CaseStatus,
      };
      commitCases(updated);
      setAdvocateActiveCase(updatedCase);
      toast.error(`Request sent to ${caseItem.cnr}`, {
        id: "request-sent",
      });
    },
    [commitCases, setAdvocateActiveCase, socket, user],
  );

  const requestSentRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!socket || !advocteActiveCase || !user) return;
    if (!isLiveCase(advocteActiveCase)) return;

    const cnr = advocteActiveCase.cnr;
    if (requestSentRef.current.has(cnr)) return;
    requestSentRef.current.add(cnr);

    socket.emit("advocate_sent_request", {
      meetingId: getMeetingId(advocteActiveCase),
      roomNo: advocteActiveCase.courtRoom,
      roomId: advocteActiveCase.courtRoomId,
      userId: user.id,
      role: user.role,
      name: user.name,
      host: false,
      cnr,
      caseNumber: advocteActiveCase.caseNumber,
      caseTitle: advocteActiveCase.caseTitle,
    });

    const updated = casesRef.current.map((c) =>
      c.cnr === cnr ? { ...c, status: "Waiting" as CaseStatus } : c,
    );
    const updatedActiveCase = updated.find((item) => item.cnr === cnr) || {
      ...advocteActiveCase,
      status: "Waiting" as CaseStatus,
    };
    commitCases(updated);
    setAdvocateActiveCase(updatedActiveCase);
  }, [user, advocteActiveCase, socket, commitCases, setAdvocateActiveCase]);

  // ------------------------------------------------------
  // 📌 DETECT PAGE NAVIGATION → LEAVE MEETING
  // ------------------------------------------------------
  // useEffect(() => {
  //   return () => {
  //     leaveMeeting();
  //   };
  // }, [routerLocation.pathname, leaveMeeting]);

  // ------------------------------------------------------
  // 🖥️ SOCKET LISTENERS
  // ------------------------------------------------------
  const updateCaseStatus = useCallback(
    (cnr: string, status: string) => {
      const updated = casesRef.current.map((c) =>
        c.cnr === cnr ? { ...c, status: status as CaseStatus } : c,
      );
      const ac = updated.find((c) => c.cnr === cnr) || null;
      const isActiveCase = advocteActiveCase?.cnr === cnr;

      commitCases(updated);

      if (ac && (isActiveCase || IN_MEETING_STATUSES.has(status))) {
        setAdvocateActiveCase(ac);
      }

      if (isActiveCase && status === "Judge Invited") {
        setShowPopover(true);
      }
    },
    [advocteActiveCase?.cnr, commitCases, setAdvocateActiveCase],
  );

  useEffect(() => {
    if (!socket) return;

    const handleJudgeInvite = (data: any) => {
      if (data.userId !== user?.id) return;
      if (data.status === "accepted") {
        toast.success("Judge accepted your request");

        updateCaseStatus(data.cnr, "Judge Invited");
      }

      if (data.status === "declined") {
        toast.error("Judge declined your request");

        updateCaseStatus(data.cnr, "Declined");
      }
    };

    const handleMessageToast = (data: any) => {
      if (data.userId === user?.id && data.message) toast.error(data.message);
    };

    const handleToggleBlur = (data: any) => {
      if (data.userId === user?.id) {
        toast.error(data.message);
        setIsBlurred((prev) => !prev);
      }
    };

    const handleToggleFreeze = (data: any) => {
      if (data.userId === user?.id) {
        toast.error(data.message);
        setIsFrozen((prev) => {
          const next = !prev;
          if (next && videoRef.current) {
            // Capture current frame to canvas when freezing
            const canvas = canvasRef.current;
            if (canvas) {
              canvas.width = videoRef.current.videoWidth;
              canvas.height = videoRef.current.videoHeight;
              canvas.getContext("2d")?.drawImage(videoRef.current, 0, 0);
              frozenFrameRef.current = canvas.toDataURL();
            }
          }
          return next;
        });
      }
    };

    const handleNextCaseNavigation = (nextCase?: CaseItem) => {
      if (nextCase) {
        setAdvocateActiveCase(nextCase);
        setIsInMeeting(false);
        startVideo();
      } else {
        closePopupOrNavigate();
      }
    };
    const handleCaseEnded = (data: any) => {
      toast.error("The case has been ended.", { id: "case-ended" });

      const updated = updateCompletedCase(casesRef.current, data?.cnr);
      const nextCase = getNextCase(updated);

      commitCases(updated as CaseItem[]);
      if (window.opener && !window.opener.closed) {
        closePopupOrNavigate();
        return;
      }
      handleNextCaseNavigation(nextCase);
    };
    // logger.log("activeCase", advocteActiveCase);
    // FIX — check both fields defensively
    const handleEndMeeting = (data: any) => {
      const matchesRoom =
        data.roomId === advocteActiveCase?.courtRoomId ||
        data.roomNo === advocteActiveCase?.courtRoom;

      if (matchesRoom) {
        toast.error("The case has been ended by the Judge.");
        leaveMeeting();
        closePopupOrNavigate();
      }
    };

    socket.on("judge_requests", handleJudgeInvite);
    socket.on("toggle-audio", handleMessageToast);
    socket.on("toggle-video", handleMessageToast);
    socket.on("toggle-blur", handleToggleBlur);
    socket.on("toggle-freeze", handleToggleFreeze);
    socket.on("case-ended", handleCaseEnded);
    socket.on("end-meeting", handleEndMeeting);
    socket.on("media-state-updated", handleMessageToast);

    return () => {
      socket.off("judge_requests", handleJudgeInvite);
      socket.off("toggle-audio", handleMessageToast);
      socket.off("toggle-video", handleMessageToast);
      socket.off("toggle-blur", handleToggleBlur);
      socket.off("toggle-freeze", handleToggleFreeze);
      socket.off("case-ended", handleCaseEnded);
      socket.off("end-meeting", handleEndMeeting);
      socket.off("media-state-updated", handleMessageToast);
    };
  }, [socket, user, updateCaseStatus, closePopupOrNavigate]);

  // ------------------------------------------------------
  // JOIN CASE
  // ------------------------------------------------------
  const handleJoinCase = (caseItem: CaseItem) => {
    const findInMeeting = cases.find(
      (c: any) =>
        c.cnr !== caseItem.cnr && IN_MEETING_STATUSES.has(String(c.status)),
    );
    if (findInMeeting)
      return toast.error("You are already in a meeting.", {
        id: "already-in-meeting",
      });
    const userdata = {
      _id: user?.id || "",
      name: user?.name || "",
      role: user?.role || "",
      roomNo: caseItem.courtRoom || "room-1",
      roomId: caseItem.courtRoomId || "",
    };
    setUserData(userdata);
    setMicOn(DEFAULT_ADVOCATE_MIC_ON);
    setLocalMicOn(DEFAULT_ADVOCATE_MIC_ON);
    localStreamRef.current
      ?.getAudioTracks()
      .forEach((track) => (track.enabled = DEFAULT_ADVOCATE_MIC_ON));
    setAdvocateActiveCase(caseItem);
    startMeetingAutoJoin(caseItem);
  };
  const startMeetingAutoJoin = (data: any) => {
    if (!socket || !user) return;

    const meetingId = data.meetingId || getMeetingId(data);

    /* 🔥 REMOVE FROM LOBBY */
    socket.emit("advocate_joined_meeting", {
      meetingId,
      userId: user.id,
      cnr: data.cnr,
    });
    updateCaseStatus(data?.cnr, "In Meeting");

    setIsInMeeting(true);
    setShowPopover(false);
  };

  // ------------------------------------------------------
  // 🚪 CLEANUP ON TAB CLOSE
  // ------------------------------------------------------
  useEffect(() => {
    const handleUnload = () => {
      if (!skipEmitRef.current) leaveMeeting();
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [leaveMeeting]);
  useEffect(() => {
    return () => {
      if (!skipEmitRef.current) leaveMeeting();
    };
  }, [leaveMeeting]);

  return (
    <div
      className={clsx(
        "flex min-h-0 flex-col gap-3 overflow-hidden p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]",
        fullscreenMode ? "h-dvh bg-blue-50" : "h-full",
      )}
    >
      <div
        className="grid min-h-0 min-w-0 flex-1 grid-cols-1 content-start gap-3 overflow-y-auto 
      lg:grid-cols-4 lg:grid-rows-1 lg:gap-6 lg:overflow-hidden"
      >
        {/* Left */}
        <div
          className={clsx("flex min-h-0 min-w-0 flex-col", {
            "lg:col-span-3": cases.length > 1,
            "lg:col-span-full": cases.length <= 1,
          })}
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl shadow-2xl">
            <div className="flex shrink-0 items-start gap-3 bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-2.5">
              <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h2 className="mb-0 flex items-center gap-2 text-sm font-semibold text-white sm:text-base">
                    <Scale className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
                    <span className="truncate">
                      {advocteActiveCase?.highCourtName}
                    </span>
                  </h2>
                  <p className="line-clamp-2 text-xs font-medium text-white/90 sm:text-sm">
                    {advocteActiveCase?.judges
                      ?.map((j: any) => j.name)
                      .join(" | ")}
                  </p>
                  <p className="truncate text-xs text-slate-200">
                    {advocteActiveCase?.courtRoom}
                  </p>
                </div>

                <div className="min-w-0 sm:text-right">
                  <h2 className="mb-0 flex items-center gap-2 text-sm font-semibold text-white sm:justify-end sm:text-base">
                    <Video className="h-4 w-4 shrink-0 sm:h-5 sm:w-5" />
                    {isInMeeting ? "Meeting Room" : "Waiting Area"}
                  </h2>
                  <p className="truncate text-xs font-medium text-white/90 sm:text-sm">
                    {advocteActiveCase?.caseTitle}
                  </p>
                  <p className="truncate text-xs text-slate-200">
                    CNR: {advocteActiveCase?.cnr}
                  </p>
                </div>
              </div>

              <Popover
                position="bottom"
                trigger={<AcceptIcon />}
                showProp={showPopover}
              >
                <h3 className="font-semibold text-gray-800 text-sm">
                  {advocteActiveCase?.caseTitle}
                </h3>
                <p className="text-gray-600 text-xs">
                  CNR: {advocteActiveCase?.cnr}
                </p>
                <button
                  type="button"
                  onClick={() => handleJoinCase(advocteActiveCase!)}
                  className="w-full mt-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded"
                >
                  Click Here To Join
                </button>
              </Popover>
            </div>

            {!isInMeeting ? (
              <div className="relative h-[45dvh] min-h-40 shrink-0 overflow-hidden bg-slate-900 sm:h-[55dvh] lg:h-auto lg:min-h-0 lg:flex-1 lg:shrink">
                {/* Hidden canvas for freeze capture */}
                <canvas ref={canvasRef} className="hidden" />

                {/* Frozen frame image */}
                {isFrozen && frozenFrameRef.current && (
                  <img
                    src={frozenFrameRef.current}
                    className="absolute inset-0 h-full w-full object-cover lg:object-cover"
                    style={{ transform: "scaleX(-1)" }}
                    alt="frozen"
                  />
                )}
                {/* Live video — hidden when frozen */}
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{
                    transform: "scaleX(-1)",
                    filter: isBlurred ? "blur(8px)" : "none",
                    display: isFrozen ? "none" : "block",
                  }}
                />

                {/* Status indicators */}
                <div className="absolute top-2 left-2 flex gap-2">
                  {isBlurred && (
                    <span className="bg-yellow-500/80 text-white text-xs px-2 py-1 rounded-full">
                      Blurred
                    </span>
                  )}
                  {isFrozen && (
                    <span className="bg-blue-500/80 text-white text-xs px-2 py-1 rounded-full">
                      Frozen
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-[65dvh] shrink-0 overflow-y-auto lg:h-auto lg:min-h-0 lg:flex-1 lg:shrink">
                <MeetingMediaSoup raisedHands={raisedHands} />
              </div>
            )}
          </div>
        </div>

        {/* Right Side Case List */}
        {cases?.length > 1 && (
          <div className="min-h-0 min-w-0 lg:col-span-1">
            <div className="flex flex-col rounded-xl border border-slate-700 bg-slate-800 shadow-2xl lg:h-full lg:min-h-0">
              <div className="shrink-0 bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-3 rounded-t-xl">
                <h2 className="text-white font-semibold flex items-center gap-2">
                  <Calendar className="w-5 h-5" /> Cases
                </h2>
              </div>

              <div className="relative p-3 sm:p-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
                {cases.map((caseItem, idx) => {
                  const canRequestJoin = isLiveCase(caseItem);

                  return (
                    <div
                      key={idx}
                      className="bg-slate-900 rounded-lg p-3 border border-slate-700 mb-3 overflow-visible"
                    >
                      <h3 className="text-white font-semibold text-sm flex gap-2 justify-between [overflow-wrap:anywhere]">
                        {caseItem.caseTitle}
                        {caseItem.status === "Judge Invited" &&
                          advocteActiveCase?.cnr !== caseItem.cnr && (
                            <div>
                              <Popover
                                position="bottom"
                                trigger={<AcceptIcon />}
                                showProp={caseItem.status === "Judge Invited"}
                              >
                                <h3 className="font-semibold text-gray-800 text-sm">
                                  {caseItem?.caseTitle}
                                </h3>
                                <p className="text-gray-600 text-xs">
                                  CNR: {caseItem?.cnr}
                                </p>
                                <button
                                  type="button"
                                  onClick={() => handleJoinCase(caseItem!)}
                                  className="w-full mt-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded"
                                >
                                  Click Here To Join
                                </button>
                              </Popover>
                            </div>
                          )}
                      </h3>

                      <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs font-medium">
                        {caseItem.status}
                      </span>

                      <p className="text-slate-400 text-xs mt-2">
                        CNR: {caseItem.cnr}
                      </p>
                      {/* <p className="text-slate-400 text-xs flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {caseItem.time}
                  </p> */}

                      <button
                        type="button"
                        onClick={() => handleSendRequest(caseItem)}
                        disabled={!canRequestJoin}
                        className={`w-full mt-3 px-3 py-2 rounded-lg text-sm ${
                          !canRequestJoin
                            ? "bg-slate-700 text-slate-500 cursor-not-allowed"
                            : "bg-gradient-to-r from-purple-600 to-blue-600 text-white"
                        }`}
                      >
                        {!canRequestJoin ? caseItem.status : "Join"}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      <AdvocateControlBar
        handRaised={handRaised}
        handPending={handPending}
        onToggleHand={toggleHand}
        micOn={micOn}
        micLocked={localMicLocked}
        camOn={camOn}
        cameraId={cameraId}
        microphoneId={microphoneId}
        onToggleMic={toggleMic}
        onToggleCam={toggleCam}
        onSelectCamera={selectCamera}
        onSelectMicrophone={selectMicrophone}
        onLeave={handleLeave}
      />
    </div>
  );
};

export default AdvocateLobby;
